// src/pages/assets/ReturnAssets.js

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    collection,
    getDocs,
    query,
    where,
} from "firebase/firestore";

import { db } from "../../firebase";

import {
    createAssetReturnRequest,
    HOLDER_TYPE
} from "../../services/assets/assetTransactionService";

import {
    WORKFLOW_STATES,
} from "../../services/assets/workflowStates";

import {
    getPendingReturns,
} from "../../services/assets/assetReplayService";

const ASSET_MASTER_COLLECTION = "asset_master";

// ======================================================
// RETURN ASSETS
// ======================================================

const ReturnAssets = () => {

    const [rows, setRows] = useState([]);
    const [persons, setPersons] = useState([]);
    const [sites, setSites] = useState([]);
    const [existingReturns, setExistingReturns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // ======================================================
    // COMPANY CONTEXT
    // ======================================================

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const company_id = localStorage.getItem("company_id") || user?.company_id;
    const currentUserId = user?.uid || user?.id || company_id;
    const currentUserName = user?.name || user?.display_name || user?.email?.split("@")[0] || currentUserId;

    // ======================================================
    // LOAD DATA
    // ======================================================

    const loadAssets = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            if (!company_id) return;

            // LOAD ASSETS
            const assetSnapshot = await getDocs(query(
                collection(db, ASSET_MASTER_COLLECTION),
                where("company_id", "==", company_id)
            ));

            const isStoreOrSM = ["sm", "owner", "manager", "store"].includes(user?.role);

    const assetList = assetSnapshot.docs
        .map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        .filter((item) => {
            // Must have a holder and not be in store
            if (!item?.current_holder_type) return false;
            if (item?.current_holder_type === HOLDER_TYPE.STORE) return false;

            // SM/Owner/Manager/Store see ALL assets outside store
            if (isStoreOrSM) return true;

            // Technician/Staff — only see assets they currently hold
            return item?.current_holder_id === currentUserId;
        });

            // LOAD PERSONS for name resolution
            const userSnapshot = await getDocs(query(
    collection(db, "users"),
    where("company_id", "==", company_id)
));
const userList = userSnapshot.docs
    .map((doc) => ({ id: doc.id, ...doc.data() }));

            // LOAD SITES for name resolution
            const siteSnapshot = await getDocs(query(
    collection(db, "sites"),
    where("company_id", "==", company_id)
));
const siteList = siteSnapshot.docs
    .map((doc) => ({ id: doc.id, ...doc.data() }));

            // LOAD PENDING RETURNS
            const returnList = await getPendingReturns(company_id);

            setRows(assetList);
            setPersons(userList);
            setSites(siteList);
            setExistingReturns(returnList);

        } catch (error) {
            console.error("LOAD_RETURNABLE_ASSETS_ERROR", error);
            setError(error?.message || "Failed to load assets");
        } finally {
            setLoading(false);
        }
    }, [company_id]);

    useEffect(() => {
        loadAssets();
    }, [loadAssets]);

    // ======================================================
    // PENDING RETURN MAP
    // ======================================================

    const pendingReturnAssetIds = useMemo(() => {
        return new Set(
            existingReturns
                .filter((item) => item?.workflow_state === WORKFLOW_STATES.RETURN_PENDING)
                .map((item) => item.asset_id)
        );
    }, [existingReturns]);

    // ======================================================
    // HOLDER NAME RESOLUTION
    // ======================================================

    const resolveHolderName = (row) => {
        if (!row) return "N/A";

        // 1. Use stored name if available
        if (row.current_holder_name) return row.current_holder_name;

        const holderId = row.current_holder_id;
        const holderType = row.current_holder_type?.toUpperCase();

        if (!holderId) return "N/A";

        // 2. Lookup from persons
        if (holderType === "STAFF" || holderType === "TECHNICIAN" || holderType === "EMPLOYEE") {
            const person = persons.find(
                (p) =>
                    (p.uid || p.id) === holderId ||
                    p.id === holderId ||
                    p.email?.split("@")[0] === holderId
            );
            if (person) {
                return person.name || person.display_name || person.email?.split("@")[0] || holderId;
            }
        }

        // 3. Lookup from sites
        if (holderType === "SITE") {
            const site = sites.find((s) => s.id === holderId);
            if (site) return site.site_name || holderId;
        }

        return holderId;
    };

    // ======================================================
    // CREATE RETURN REQUEST
    // ======================================================

    const handleReturn = async (row) => {
        try {
            if (!row?.id) throw new Error("Invalid asset selected");
            if (!company_id) return;

            if (pendingReturnAssetIds.has(row.id)) {
                setError("Return request already pending for this asset");
                return;
            }

            setActionLoadingId(row.id);
            setError("");
            setSuccess("");

            const fromHolderName = resolveHolderName(row);

            await createAssetReturnRequest({
                asset_id:         row.id,
                from_type:        row.current_holder_type,
                from_id:          row.current_holder_id,
                from_holder_name: fromHolderName,
                requested_by:     currentUserId,
                requested_by_name: currentUserName,
                remarks:          "Asset return request",
                company_id,
            });

            setSuccess(`Return request created for ${row.asset_name || "asset"}`);
            await loadAssets();

        } catch (error) {
            console.error("CREATE_RETURN_REQUEST_ERROR", error);
            setError(error?.message || "Failed to create return request");
        } finally {
            setActionLoadingId(null);
        }
    };

    // ======================================================
    // UI
    // ======================================================

    return (
        <div style={{ padding: "24px", background: "#f8fafc", minHeight: "100vh" }}>

            {/* HEADER */}
            <div style={{ marginBottom: "24px" }}>
                <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "700", color: "#111827" }}>
                    Return Assets
                </h2>
                <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: "14px" }}>
                    Initiate asset return to store
                </p>
            </div>

            {/* ERROR */}
            {error && (
                <div style={{
                    background: "#fef2f2", color: "#dc2626",
                    padding: "12px 16px", borderRadius: "10px",
                    marginBottom: "16px", fontSize: "14px",
                    border: "1px solid #fecaca",
                }}>
                    {error}
                </div>
            )}

            {/* SUCCESS */}
            {success && (
                <div style={{
                    background: "#f0fdf4", color: "#16a34a",
                    padding: "12px 16px", borderRadius: "10px",
                    marginBottom: "16px", fontSize: "14px",
                    border: "1px solid #bbf7d0",
                }}>
                    {success}
                </div>
            )}

            {/* LOADING */}
            {loading ? (
                <div style={{
                    background: "#fff", borderRadius: "14px",
                    padding: "48px", textAlign: "center",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                }}>
                    <div style={{ color: "#6b7280", fontSize: "15px" }}>Loading assets...</div>
                </div>
            ) : rows.length === 0 ? (
                <div style={{
                    background: "#fff", borderRadius: "14px",
                    padding: "48px", textAlign: "center",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                }}>
                    <div style={{ fontSize: "32px", marginBottom: "12px" }}>📦</div>
                    <div style={{ color: "#374151", fontWeight: "600", fontSize: "16px" }}>
                        No assets available for return
                    </div>
                    <div style={{ color: "#9ca3af", fontSize: "14px", marginTop: "4px" }}>
                        All assets are currently in store
                    </div>
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {rows.map((row) => {
                        const hasPendingReturn = pendingReturnAssetIds.has(row.id);
                        const holderName = resolveHolderName(row);
                        const isProcessing = actionLoadingId === row.id;

                        return (
                            <div key={row.id} style={{
                                background: "#fff",
                                borderRadius: "14px",
                                padding: "20px 24px",
                                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                                border: hasPendingReturn
                                    ? "1px solid #fde68a"
                                    : "1px solid #f3f4f6",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "16px",
                                flexWrap: "wrap",
                            }}>

                                {/* LEFT — ASSET INFO */}
                                <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}>

                                    {/* ICON */}
                                    <div style={{
                                        width: "44px", height: "44px",
                                        borderRadius: "10px",
                                        background: hasPendingReturn ? "#fef3c7" : "#eff6ff",
                                        display: "flex", alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "20px", flexShrink: 0,
                                    }}>
                                        {hasPendingReturn ? "⏳" : "🔧"}
                                    </div>

                                    {/* DETAILS */}
                                    <div>
                                        <div style={{
                                            fontWeight: "700", fontSize: "15px",
                                            color: "#111827", marginBottom: "2px",
                                        }}>
                                            {row.asset_name || "Unnamed Asset"}
                                        </div>
                                        <div style={{
                                            fontSize: "13px", color: "#6b7280",
                                        }}>
                                            {row.serial_no && (
                                                <span style={{ marginRight: "12px" }}>
                                                    # {row.serial_no}
                                                </span>
                                            )}
                                            {row.asset_category && (
                                                <span>{row.asset_category}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* MIDDLE — HOLDER INFO */}
                                <div style={{
                                    display: "flex", gap: "32px",
                                    flexWrap: "wrap", alignItems: "center",
                                }}>

                                    <div>
                                        <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "2px", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                            Holder
                                        </div>
                                        <div style={{ fontSize: "14px", fontWeight: "600", color: "#374151" }}>
                                            {holderName}
                                        </div>
                                    </div>

                                    <div>
                                        <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "2px", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                            Type
                                        </div>
                                        <div style={{
                                            fontSize: "12px", fontWeight: "600",
                                            color: "#6366f1",
                                            background: "#eef2ff",
                                            padding: "2px 10px",
                                            borderRadius: "999px",
                                            display: "inline-block",
                                        }}>
                                            {row.current_holder_type || "N/A"}
                                        </div>
                                    </div>

                                    <div>
                                        <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "2px", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                            Status
                                        </div>
                                        <div style={{
                                            fontSize: "12px", fontWeight: "600",
                                            color: hasPendingReturn ? "#d97706" : "#16a34a",
                                            background: hasPendingReturn ? "#fef3c7" : "#f0fdf4",
                                            padding: "2px 10px",
                                            borderRadius: "999px",
                                            display: "inline-block",
                                        }}>
                                            {hasPendingReturn ? "Return Pending" : "Active"}
                                        </div>
                                    </div>

                                </div>

                                {/* RIGHT — ACTION */}
                                <div style={{ flexShrink: 0 }}>
                                    <button
                                        onClick={() => handleReturn(row)}
                                        disabled={hasPendingReturn || isProcessing}
                                        style={{
                                            padding: "10px 20px",
                                            borderRadius: "8px",
                                            border: "none",
                                            fontWeight: "600",
                                            fontSize: "14px",
                                            cursor: hasPendingReturn || isProcessing ? "not-allowed" : "pointer",
                                            background: hasPendingReturn
                                                ? "#f3f4f6"
                                                : isProcessing
                                                    ? "#93c5fd"
                                                    : "#1e40af",
                                            color: hasPendingReturn ? "#9ca3af" : "#fff",
                                            transition: "background 0.2s",
                                            minWidth: "100px",
                                        }}
                                    >
                                        {isProcessing
                                            ? "Creating..."
                                            : hasPendingReturn
                                                ? "Pending"
                                                : "Return"}
                                    </button>
                                </div>

                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ReturnAssets;